import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, RefreshCw, Save, Globe } from "lucide-react";
import { siteStatsDisplay } from "../../lib/api";

const FIELDS = [
    { key: "sigorta_dosya_sayisi", label: "Sigorta dosyası" },
    { key: "muvekkil_sayisi", label: "Müvekkil kaydı" },
    { key: "deger_kaybi_sayisi", label: "Değer kaybı kaydı" },
    { key: "deger_kaybi_30_gun", label: "Son 30 gün (DK)" },
];

export default function SiteStatsAyarPage({ embedded = false }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [real, setReal] = useState({});
    const [values, setValues] = useState({});
    const [useOverride, setUseOverride] = useState(false);
    const [note, setNote] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await siteStatsDisplay.get();
            setReal(d.real || {});
            setValues(d.override?.values || d.display || {});
            setUseOverride(Boolean(d.use_override));
            setNote(d.override?.note || "");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "İstatistikler yüklenemedi.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    async function save() {
        setSaving(true);
        try {
            await siteStatsDisplay.save({
                use_override: useOverride,
                values,
                note,
            });
            toast.success("Kamu site istatistikleri kaydedildi.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kayıt başarısız.");
        } finally {
            setSaving(false);
        }
    }

    function syncFromDb() {
        setValues({ ...real });
        toast.info("Gerçek DB sayıları forma aktarıldı.");
    }

    return (
        <div
            className={
                embedded
                    ? "p-3 lg:p-5 max-w-3xl animate-fade-in"
                    : "p-6 lg:p-8 max-w-3xl mx-auto animate-fade-in"
            }
        >
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    {!embedded ? (
                        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                            Kamu site
                        </div>
                    ) : null}
                    <h1
                        className={`font-display text-white mt-1 flex items-center gap-2 ${
                            embedded ? "text-xl" : "text-3xl"
                        }`}
                    >
                        <BarChart3 className="h-8 w-8 text-[#D4AF37]" />
                        Anasayfa istatistikleri
                    </h1>
                    <p className="text-sm text-slate-400 mt-2">
                        mbshukuk.com anasayfasındaki sayılar. Varsayılan: canlı DB.
                        Özelleştirme açıkken paneldeki değerler gösterilir.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="h-10 px-3 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-sm inline-flex items-center gap-2"
                >
                    <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    Yenile
                </button>
            </div>

            <label className="flex items-center gap-3 mb-6 p-4 rounded-xl border border-white/10 bg-[#121A2F] cursor-pointer">
                <input
                    type="checkbox"
                    checked={useOverride}
                    onChange={(e) => setUseOverride(e.target.checked)}
                    className="rounded border-white/20"
                />
                <span className="text-sm text-slate-200">
                    Özelleştirilmiş sayıları kamu sitede göster (DB yerine panel değerleri)
                </span>
            </label>

            <div className="space-y-4 mb-6">
                {FIELDS.map((f) => (
                    <div
                        key={f.key}
                        className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center p-4 rounded-xl border border-white/10 bg-[#0a1018]"
                    >
                        <div className="text-sm text-slate-300">{f.label}</div>
                        <div className="text-xs text-slate-500">
                            DB:{" "}
                            <span className="font-mono text-slate-300">
                                {real[f.key] ?? "—"}
                            </span>
                        </div>
                        <input
                            type="number"
                            min={0}
                            value={values[f.key] ?? ""}
                            onChange={(e) =>
                                setValues((s) => ({
                                    ...s,
                                    [f.key]: e.target.value === "" ? "" : Number(e.target.value),
                                }))
                            }
                            className="h-10 px-3 rounded-md bg-[#0B0F19] border border-white/10 text-sm font-mono"
                        />
                    </div>
                ))}
            </div>

            <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="İç not (isteğe bağlı)"
                rows={2}
                className="w-full mb-4 px-3 py-2 rounded-md bg-[#0B0F19] border border-white/10 text-sm text-slate-300"
            />

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="btn-gold h-10 px-4 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-60"
                >
                    <Save className="h-4 w-4" />
                    Kaydet
                </button>
                <button
                    type="button"
                    onClick={syncFromDb}
                    className="h-10 px-4 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-sm"
                >
                    DB sayılarını kopyala
                </button>
                <a
                    href="https://mbshukuk.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-10 px-4 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-sm inline-flex items-center gap-2"
                >
                    <Globe className="h-4 w-4" />
                    Canlı site
                </a>
                <a
                    href="https://yeni.mbshukuk.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-10 px-4 rounded-md border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 text-sm inline-flex items-center gap-2"
                >
                    Önizleme (yeni)
                </a>
            </div>
        </div>
    );
}
